import { short } from "diagnostics-lib/io";
import * as io from "io-ts";
import type { Converter, ConverterResult } from "../types";
import { ConverterErrorType, ConverterResultIOTS } from "../types";
import { isLeft } from "fp-ts/lib/Either";

export const convertSourceSnapshotsJSONToJSON: Converter = (input: string) => {
  let result: ConverterResult;
  try {
    result = JSON.parse(input);
  } catch (err) {
    return {
      result: { sourceId: undefined, snapshots: [] },
      errors: [
        {
          kind: ConverterErrorType.MALFORMED_JSON,
          explain: "malformed json",
          location: { file: "", line: 0 },
          data: { value: input },
        },
      ],
    };
  }

  if (isLeft(ConverterResultIOTS.decode(result))) {
    return {
      result: { sourceId: undefined, snapshots: [] },
      errors: [
        {
          kind: ConverterErrorType.INVALID_JSON,
          explain: "invalid json",
          location: { file: "", line: 0 },
          data: { value: input },
        },
      ],
    };
  }

  const duplicatedDate = result.snapshots
    .map(({ date }) => date)
    .filter((date, i, arr) => arr.indexOf(date) !== i);
  if (duplicatedDate.length > 0) {
    return {
      result: { sourceId: undefined, snapshots: [] },
      errors: [
        {
          kind: ConverterErrorType.DUPLICATED_SNAPSHOT_DATE,
          explain: "duplicated snapshot date in import",
          location: { file: "", line: 0 },
          data: { value: duplicatedDate.toString() },
        },
      ],
    };
  }

  const invalidValues = result.snapshots.flatMap(snapshot =>
    snapshot.sensorValues
      .filter(
        ({ mean, std, rawValuesCount }) =>
          Number.isNaN(Number(mean)) ||
          (std && Number.isNaN(Number(std))) ||
          (typeof std === "number" && std < 0) ||
          (typeof rawValuesCount === "number" && rawValuesCount <= 0),
      )
      .map(({ sensorTag, mean, std, rawValuesCount }) => ({
        kind: ConverterErrorType.INVALID_SENSOR_VALUE,
        explain: "invalid sensor value",
        location: { file: "", line: 0 },
        data: {
          key: sensorTag,
          value: snapshot.date || snapshot.acquisitionStartDate,
          hint: `mean: ${mean}, std: ${std}, rawValuesCount: ${rawValuesCount}`,
        },
      })),
  );

  const invalidLabels = result.snapshots
    .filter(snap => snap.label && isLeft(short(io.string).decode(snap.label)))
    .map(snap => ({
      kind: ConverterErrorType.INVALID_LABEL_TOO_LONG,
      explain: "invalid label too long",
      location: { file: "", line: 0 },
      data: { value: snap.label },
    }));

  const invalidGroups = result.snapshots
    .filter(snap => snap.group && isLeft(short(io.string).decode(snap.group)))
    .map(snap => ({
      kind: ConverterErrorType.INVALID_GROUP_TOO_LONG,
      explain: "invalid group too long",
      location: { file: "", line: 0 },
      data: { value: snap.group },
    }));

  const errors = [
    ...(result.sourceId
      ? isLeft(short(io.string).decode(result.sourceId))
        ? [
            {
              kind: ConverterErrorType.INVALID_SOURCE_ID,
              explain: "invalid source id",
              location: { file: "", line: 0 },
              data: { value: result.sourceId },
            },
          ]
        : []
      : [
          {
            kind: ConverterErrorType.MISSING_SOURCE_ID,
            explain: "missing sourceID",
            location: { file: "", line: 0 },
            data: { value: result },
          },
        ]),
    ...invalidValues,
    ...invalidLabels,
    ...invalidGroups,
  ];

  return {
    result,
    errors,
  };
};
